import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { Component, ElementRef, Inject, Input, OnDestroy, Optional, Self, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, NgControl, Validators } from '@angular/forms';
import { MatFormField, MatFormFieldControl, MAT_FORM_FIELD } from '@angular/material/form-field';
import { Subject } from 'rxjs';

/*
  Much thanks to https://material.angular.io/guide/creating-a-custom-form-field-control for the entirety of this component
*/

export class Tel {
  constructor(
    public area: string = '',
    public exchange: string = '',
    public subscriber: string = ''
  ) {
    
  }

  // ###-###-####
  public toString(): string {
    return this.area + '-' + this.exchange + '-' + this.subscriber;
  }

  // check if number is a valid phone number
  public isValid(): boolean {
    return !!this.area && !!this.exchange && !!this.subscriber;
  }

  // convert object to phone number
  public static transformAnonymous(phoneNum: any): Tel {
    if (!phoneNum) {
      return new Tel();
    }
    return new Tel(phoneNum.area ?? '', phoneNum.exchange ?? '', phoneNum.subscriber ?? '');
  }
}

@Component({
    selector: 'app-tel-input',
    templateUrl: './tel-input.component.html',
    styleUrls: ['./tel-input.component.css'],
    providers: [{ provide: MatFormFieldControl, useExisting: TelInputComponent }],
    host: {
        '[class.example-floating]': 'shouldLabelFloat',
        '[id]': 'id',
    },
    standalone: false
})
export class TelInputComponent implements ControlValueAccessor, MatFormFieldControl<Tel>, OnDestroy {
  static nextId: number = 0;
  @ViewChild('area') areaInput!: HTMLInputElement;
  @ViewChild('exchange') exchangeInput!: HTMLInputElement;
  @ViewChild('subscriber') subscriberInput!: HTMLInputElement;

  parts = this._formBuilder.group({
    area: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(3)]],
    exchange: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(3)]],
    subscriber: ['', [Validators.required, Validators.minLength(4), Validators.maxLength(4)]],
  });
  stateChanges: Subject<void> = new Subject<void>();
  focused: boolean = false;
  touched: boolean = false;
  controlType: string = 'example-tel-input';
  id: string = `example-tel-input-${TelInputComponent.nextId++}`;
  onChange = (_: any) => { };
  onTouched = () => { };

  get empty(): boolean {
    const {
      value: { area, exchange, subscriber },
    } = this.parts;

    return !area && !exchange && !subscriber;
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  @Input('aria-describedby') userAriaDescribedBy!: string;

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }
  set placeholder(value: string) {
    this._placeholder = value;
    this.stateChanges.next();
  }
  private _placeholder: string = '';

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required: boolean = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.parts.disable() : this.parts.enable();
    this.stateChanges.next();
  }
  private _disabled: boolean = false;

  @Input()
  get value(): Tel | null {
    if (this.parts.valid) {
      const {
        value: { area, exchange, subscriber },
      } = this.parts;
      return new Tel(area!, exchange!, subscriber!);
    }
    return null;
  }
  set value(tel: Tel | null) {
    const { area, exchange, subscriber } = tel || new Tel();
    this.parts.setValue({ area, exchange, subscriber });
    this.stateChanges.next();
  }

  get errorState(): boolean {
    return this.parts.invalid && this.touched;
  }

  constructor(
    private _formBuilder: FormBuilder,
    private _focusMonitor: FocusMonitor,
    private _elementRef: ElementRef<HTMLElement>,
    @Optional() @Inject(MAT_FORM_FIELD) public _formField: MatFormField,
    @Optional() @Self() public ngControl: NgControl,
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  onFocusIn(): void {
    if (!this.focused) {
      this.focused = true;
      this.stateChanges.next();
    }
  }

  onFocusOut(event: FocusEvent): void {
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  autoFocusNext(control: AbstractControl, nextElement?: HTMLInputElement): void {
    if (!control.errors && nextElement) {
      this._focusMonitor.focusVia(nextElement, 'program');
    }
  }

  autoFocusPrev(control: AbstractControl, prevElement: HTMLInputElement): void {
    if (control.value.length < 1) {
      this._focusMonitor.focusVia(prevElement, 'program');
    }
  }

  setDescribedByIds(ids: string[]): void {
    const controlElement: Element = this._elementRef.nativeElement.querySelector(
      '.example-tel-input-container',
    )!;
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  onContainerClick(): void {

    if (this.parts.controls.subscriber.valid || this.parts.controls.exchange.valid) {
      this._focusMonitor.focusVia(this.subscriberInput, 'program');
      return;
    }

    if (this.parts.controls.area.valid) {
      this._focusMonitor.focusVia(this.exchangeInput, 'program');
      return;
    }

    this._focusMonitor.focusVia(this.areaInput, 'program');

  }

  writeValue(tel: Tel | null): void {
    this.value = tel;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  _handleInput(control: AbstractControl, nextElement?: HTMLInputElement): void {
    control.setValue(control.value.replace(/[^0-9]/g, ''), { onlySelf: true, emitEvent: false });
    this.autoFocusNext(control, nextElement);
    this.onChange(this.value);
  }
}
